import { useToggle } from "@uidotdev/usehooks";
import { cn } from "cn";
import { capitalize } from "lodash-es";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { BwCheckmarkLine, BwChevronDownLine } from "@/components/ui/icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useGetGlobalOffice } from "@/features/office/_api/use-global-office";
import { Typography } from "../ui/typography";

/** The fields every purpose source guarantees. */
export type PurposeOption = {
  id: string;
  title: string;
};

type PurposeSelectProps = {
  className?: string;
  value: PurposeOption;
  onChange: (value: PurposeOption) => void;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
  disabled?: boolean;
};

export default function PurposeSelect({
  className,
  value,
  onChange,
  defaultOpen = false,
  onOpenChange,
  modal = true,
  disabled,
  ...props
}: PurposeSelectProps) {
  const { data: globalValues } = useGetGlobalOffice();
  const purposeOptions = globalValues?.purposes || [];
  const [open, toggleOpen] = useToggle(defaultOpen);

  const handleOpenChange = (open: boolean) => {
    onOpenChange?.(open);
    toggleOpen(open);
  };

  return (
    <Popover modal={modal} open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger disabled={disabled} className="group" asChild>
        <div
          role="combobox"
          className={cn(
            /* sorted */
            "flex items-center justify-between",
            "w-full min-h-10",
            "px-3 py-2",
            "cursor-pointer rounded-lg outline outline-outline-dark-low",
            "aria-invalid:outline-outline-negative-max data-[state=open]:outline-outline-dark-max data-[state=open]:outline-[1.5px] group-disabled:cursor-not-allowed group-disabled:bg-bg-dark-low group-disabled:opacity-50",
            !value && "text-text-dark-min",
          )}
          {...props}
        >
          {value ? (
            <Typography
              className="flex items-center gap-2 text-text-dark"
              size="sm"
            >
              {capitalize(value.title)}
            </Typography>
          ) : (
            <Typography className="text-text-dark-low" size="sm">
              Select purpose
            </Typography>
          )}

          <BwChevronDownLine
            size={16}
            className="text-text-dark-high shrink-0"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent className="p-0">
        <Command shouldFilter={false} tabIndex={0}>
          <CommandList className="outline-none">
            <CommandEmpty>No purpose found.</CommandEmpty>
            <CommandGroup>
              {purposeOptions.map((purpose) => (
                <CommandItem
                  value={purpose.title}
                  key={purpose.id}
                  onSelect={() => {
                    // Set value to form field of the parent
                    onChange(purpose);
                    handleOpenChange(false);
                  }}
                >
                  <div className="flex items-center justify-between w-full">
                    <Typography
                      className="flex items-center gap-2 text-text-dark"
                      size="sm"
                    >
                      {capitalize(purpose.title)}
                    </Typography>

                    <BwCheckmarkLine
                      size={16}
                      className={cn(
                        /* sorted */
                        "shrink-0",
                        value?.id === purpose.id ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
